import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import amelioRoundedLogo from "../../../assets/images/amelio-rounded-logo.png";
import axios from "../../../util/axiosconfig";
import { ProgressSpinner } from "primereact/progressspinner";
import CryptoJS from "crypto-js";
import { showError } from "../../../util/ShowStatus";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";

interface Module {
  moduleID: number;
  moduleName?: string;
  display_name?: string;
  moduleDescription?: string;
  isActive?: boolean;
}

const SelectModule: React.FC = () => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);

  const [modules, setModules] = useState<Module[]>([]);
  const [availableModules, setAvailableModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [availableModuleIDs, setAvailableModuleIDs] = useState<string[]>([]);

  useEffect(() => {
    const moduleIDs =
      localStorage.moduleID?.split(",").map((id: string) => id.trim()) || [];

    if (localStorage.userEmail === "jakub@ameliogroup.co.uk") {
      moduleIDs.push("2");
    }

    setAvailableModuleIDs(moduleIDs);
  }, []);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        setLoading(true);
        const response = await axios.get("modules");
        setModules(response.data.result);
        setLoading(false);
      } catch (error: any) {
        console.error(error);
        showError(error?.response?.data?.message, toast, error?.message);
        setLoading(false);
      }
    };

    fetchModules(); //19025
  }, []);

  useEffect(() => {
    if (modules.length > 0) {
      const filteredModules = modules.filter((module) =>
        availableModuleIDs.includes(module.moduleID.toString())
      );
      setAvailableModules(filteredModules);
    }
  }, [modules, availableModuleIDs]);

  useEffect(() => {
    if (availableModules.length === 1) {
      handleModuleClick(availableModules[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableModules]);

  const handleModuleClick = async (module: Module) => {
    try {
      setLoading(true);
      localStorage.setItem("currentModuleID", JSON.stringify(module.moduleID));
      const response = await axios.get(
        `user-role/web-menu?moduleID=${module.moduleID}`
      );
      setLoading(false);
      // Encrypt the menu items and store them in local storage
      const encryptedMenuItems = CryptoJS.AES.encrypt(
        JSON.stringify(response.data.result),
        process.env.REACT_APP_MENU_SECRET_KEY
      ).toString();
      localStorage.setItem("encryptedMenuItems", encryptedMenuItems);

      const moduleUrl =
        localStorage.userEmail === "jakub@ameliogroup.co.uk" &&
        module.moduleID.toString() === "2"
          ? "generate-report"
          : Array.isArray(response.data.result) &&
            response.data.result.length > 0
          ? response.data.result[0].url
          : null;
      if (moduleUrl) {
        localStorage.setItem("currentModuleUrl", `/layout/${moduleUrl}`);
        navigate(`/layout/${moduleUrl}`);
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const Logout = () => {
    localStorage.clear();
    navigate("/");
  };

  return (
    <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-white tw-p-4">
      <Toast ref={toast} />
      <div className="tw-absolute tw-top-4 tw-right-4 tw-z-10">
        <Button
          label="Logout"
          icon="pi pi-sign-out"
          id="logoutBtn"
          className={`tw-p-button-rounded tw-p-button-text tw-text-white tw-bg-[#152846] tw-p-2 hover:tw-bg-[#0d192c]`}
          onClick={Logout}
        >
        </Button>
      </div>
      {loading ? (
        <div className="tw-h-full tw-w-full tw-bg-transparent tw-flex tw-justify-center tw-items-center tw-absolute tw-top-0 tw-left-0 tw-z-50">
          <ProgressSpinner />
        </div>
      ) : (
        <div className="tw-w-full tw-max-w-6xl">
          <h1 className="tw-text-3xl tw-font-bold tw-text-gray-800 tw-text-center tw-mb-8">
            Select Module
          </h1>
          {availableModules.length === 0 ? (
            <p className="tw-text-lg tw-text-gray-600 tw-text-center">
              No modules available.
            </p>
          ) : (
            <div className="tw-flex tw-justify-center tw-items-center tw-flex-wrap tw-gap-6">
              {availableModules.map((module) => (
                <div
                  key={module.moduleID}
                  onClick={() => handleModuleClick(module)}
                  className="tw-bg-[#152846] tw-relative tw-shadow-md tw-w-80 tw-h-80 tw-flex tw-flex-col tw-rounded-full tw-items-center tw-justify-center tw-cursor-pointer tw-transition tw-transform hover:tw-scale-105 hover:tw-shadow-lg tw-p-4"
                >
                  <img
                    src={amelioRoundedLogo}
                    alt="Amelio Logo In Background"
                    className="tw-absolute tw-z-0 tw-inset-0 tw-w-full tw-h-full tw-opacity-100"
                  />
                  <h2 className="tw-text-xl tw-z-10 tw-max-w-32 tw-font-semibold tw-text-white tw-text-center tw-overflow-hidden">
                    {module.display_name}
                  </h2>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SelectModule;
